// src/services/liveMessages.js — the document going away is not the message going away.
//
// 🟥 A MESSAGE APPEARED, VANISHED, AND CAME BACK ON THE NEXT SYNC.
//    "when I text someone from the app my message shows up immediately in the
//     web, then it disappears, then re-appears on the next sync."
//
//    Three events, in this order, and all of them working as written:
//
//      1. the phone writes the document  → the live query delivers it  → drawn
//      2. the phone caches it in SQLite and store-and-forward DELETES the
//         document ~50ms later → the next snapshot has one row fewer →
//         `watchMessages` rebuilt the list from the snapshot → the row is gone
//      3. the mailbox is drained on the next open or focus → it comes back
//
// 🟥 THE FAULT IS STEP 2 TREATING A HANDOFF AS A DELETION. The sweep does not
//    mean "this message is gone", it means "the phone has it now". The phone
//    behaves correctly here because its SQLite copy is not touched by the sweep;
//    the browser had no equivalent, so it believed the empty cloud.
//
// 🟢 SO A ROW THAT HAS BEEN SEEN IS KEPT UNTIL SOMETHING SAYS IT IS GONE — and a
//    bare disappearance says nothing. A REAL deletion is not a disappearance: it
//    is a tombstone (`isDeleted`) or a `deletedFor` entry, both of which arrive
//    as an UPDATE and survive the sweep precisely so they can be seen.

/* The live window is 200; retaining a little beyond it costs nothing and stops a
   long-lived tab from growing without bound. */
const RETAIN_MAX = 400;

/**
 * Fold a snapshot into what is already on screen.
 *
 * @param prev  rows currently held (any iterable of messages)
 * @param rows  the rows this snapshot delivered
 * @returns the union, snapshot values winning for ids present in both
 */
export function mergeLive(prev, rows) {
  const out = new Map();
  for (const m of prev || []) if (m && m.id) out.set(m.id, m);

  /* 🟢 THE SNAPSHOT WINS FOR ANYTHING IT STILL HAS. Receipts, reactions and
     edits all arrive as updates to a row we already hold, so the fresh copy has
     to replace the stale one rather than merely being absent from the removal
     set. */
  for (const m of rows || []) if (m && m.id) out.set(m.id, m);

  if (out.size <= RETAIN_MAX) return [...out.values()];
  /* drop the oldest retained rows first — they are furthest outside the window */
  return [...out.values()].slice(out.size - RETAIN_MAX);
}

/**
 * Is this row one the reader should no longer see?
 *
 * 🟥 THIS IS THE ONLY THING THAT MAY REMOVE A MESSAGE, and none of it is "the
 *    document is missing". A tombstone survives the sweep on purpose.
 */
export function isGone(m, myUid) {
  if (!m) return true;
  if (m.isDeleted === true) return true;
  if (Array.isArray(m.deletedFor) && myUid && m.deletedFor.includes(myUid)) return true;
  return false;
}
